import type {
  JobStatus,
  RallyMarker,
  TimeRange,
  V1Source,
} from "../composables/useJobs";
import { isV1Job, type KindedJob } from "./inputKind";
import { formatDecimalBytes } from "./format";

/**
 * The one derived display rule for customer-facing done-ness (see
 * docs/design/segment-web-customer-done-adoption-one-pager.md).
 *
 * The backend splits "done" into `customer_done` (the 360p preview is
 * delivered — uploaded + duration-QA passed) and internal completion
 * (`status === "completed"`, ~25–40s later after operator artifacts
 * finalize). Every SPA surface that shows done-ness — status badge,
 * tab title, detail-view branches, the preview player gate — consumes
 * these helpers so they can never disagree.
 *
 * C-W1: `customer_done` is the ONLY done signal. Never infer done-ness
 * from `artifacts.preview`, percentages, or stage names — the server
 * derives `customer_done` from the persisted snapshot precisely so
 * clients key on one field (#1092 C-D1).
 *
 * C-W6 (deploy skew): `customer_done` is optional on the wire. Against
 * a pre-#1092 backend it is absent → `displayDone` is false →
 * `displayStatus` passes the raw status through, which is exactly
 * today's behavior.
 */

/** The minimal shape both `JobSummary` and `JobDetail` satisfy. */
export interface DisplayableJob {
  status: JobStatus;
  customer_done?: boolean | null;
}

/** True the moment the customer's deliverable (360p preview) is ready. */
export function displayDone(job: DisplayableJob): boolean {
  return job.customer_done === true;
}

/**
 * The status the UI should render. `processing` + `customer_done` maps
 * to `completed`; everything else (incl. `failed` — C-W3) passes
 * through unchanged.
 */
export function displayStatus(job: DisplayableJob): JobStatus {
  return job.status === "processing" && displayDone(job)
    ? "completed"
    : job.status;
}

// ---------------------------------------------------------------------
// RallyClip v1 result display (issue #1866, M-13a). Two ORTHOGONAL axes:
//
//   analysis state — what the server says about the analysis
//     (pending / processing / completed with rallies / completed empty
//     / failed), driven by the checked ``customer_done``,
//     ``empty_result`` and ``num_segments`` fields.
//   media availability — whether THIS browser can play anything
//     (cloud preview for v0, an attached local file for v1, or nothing).
//
// Keeping them apart is what makes "no local video" render as
// unavailable playback rather than as a failed job or a false
// "No rallies found" (device-lifecycle.md "Other devices and a missing
// video"). v0 keeps its existing gates unchanged; the new values only
// appear for v1 jobs.
// ---------------------------------------------------------------------

/** Shape the analysis/media derivations read. Both list rows and the
 *  detail response satisfy it (extra fields are optional). */
export interface ResultJob extends DisplayableJob, KindedJob {
  empty_result?: boolean | null;
  num_segments?: number | null;
  highlights_available?: boolean | null;
  rally_markers?: RallyMarker[] | null;
  source?: V1Source | null;
  error_code?: string | null;
  can_retry?: boolean;
  retry_block_reason?: string | null;
}

export type AnalysisState =
  | "pending_upload"
  | "processing"
  | "completed"
  | "completed_empty"
  | "failed";

/**
 * The analysis-state axis. ``completed_empty`` is the CHECKED zero-rally
 * success (server ``empty_result === true``); everything the server
 * reports as done with a nonzero count is ``completed``. Never derived
 * from highlight URLs, ``hl_start_s`` or media presence.
 */
export function analysisState(job: ResultJob): AnalysisState {
  const status = displayStatus(job);
  if (status === "completed" && job.empty_result === true) {
    return "completed_empty";
  }
  return status;
}

/**
 * The M-13b attachment seam (issue #1867): a local file the browser
 * has matched to this job's source, plus the range it can play. M-13a
 * never populates it — no attach control, no equivalent-file matching,
 * no cloud sync — but every media derivation already accepts it so the
 * follow-on task plugs in without touching the state rules here.
 */
export interface LocalMediaAttachment {
  /** Object/blob URL for a `<video src>` the browser already holds. */
  url: string;
  /** The playable range of that file on the original video's clock. */
  playableRange?: TimeRange | null;
}

export type MediaAvailability =
  /** v0: the cloud 360p preview is servable through /highlights. */
  | "cloud_preview"
  /** v1: a local file is attached (M-13b); play source ranges from it. */
  | "local_file"
  /** v1 with rallies but no attached file: results only, no playback. */
  | "unattached"
  /** Nothing to play and nothing missing: empty result, not done yet,
   *  or a v0 job with no servable preview (today's blank). */
  | "none";

/**
 * The media-availability axis. The v0 arm is EXACTLY the pre-existing
 * player gate (uiStatus completed, not empty, mid-processing
 * customer_done or terminal highlights_available) so v0 playback is
 * unchanged; v1 never reaches the cloud arm because no cloud highlights
 * exist for it (api-schemas.md: ``highlights_url`` is null).
 */
export function mediaAvailability(
  job: ResultJob,
  local: LocalMediaAttachment | null | undefined = null,
): MediaAvailability {
  const state = analysisState(job);
  if (state !== "completed") return "none";
  if (isV1Job(job)) {
    return local ? "local_file" : "unattached";
  }
  const cloudPreview =
    job.status === "processing" || job.highlights_available === true;
  return cloudPreview ? "cloud_preview" : "none";
}

/**
 * The one permanently unretryable v1 failure: the saved bundle matched
 * its creation size/hash but is structurally malformed. Identified from
 * the saved ``input_kind`` + ``error_code`` pair, exactly as the API
 * does (api-schemas.md "Retry a failed job"). v0's INVALID_ARGUMENT is
 * a different failure and is untouched.
 */
export function isVerifiedMalformedV1Input(job: ResultJob): boolean {
  return isV1Job(job) && job.error_code === "INVALID_ARGUMENT";
}

export type RetryAffordance = "shown" | "hidden";

/**
 * Whether the failed panel renders a Retry control at all. The server
 * owns eligibility: ``can_retry=false`` with the v1 ``invalid_input``
 * block (or the equivalent saved kind+code pair) HIDES the control,
 * because no retry of the same bytes can succeed. Every other
 * ``can_retry=false`` keeps today's disabled-button-with-tooltip, and
 * ``can_retry=true`` keeps the enabled button.
 */
export function retryAffordance(job: ResultJob): RetryAffordance {
  if (job.status !== "failed") return "hidden";
  if (
    job.can_retry === false &&
    (job.retry_block_reason === "invalid_input" ||
      isVerifiedMalformedV1Input(job))
  ) {
    return "hidden";
  }
  return "shown";
}

/** One rally's window on the original video, for the v1 rally list and
 *  the M-13b seek seam. Identity is the server's integer ``rally``. */
export interface RallySourceRange {
  rally: number;
  start_seconds: number;
  end_seconds: number;
}

/**
 * The per-rally ``source_range`` markers of a v1 result, in marker
 * order. Markers without a well-formed range (a v0 marker, or a
 * malformed one) are skipped rather than invented; identities are
 * preserved, never re-numbered.
 */
export function rallySourceRanges(job: ResultJob): RallySourceRange[] {
  const markers = job.rally_markers;
  if (!Array.isArray(markers)) return [];
  const out: RallySourceRange[] = [];
  for (const marker of markers) {
    if (!marker || typeof marker !== "object") continue;
    const range = marker.source_range;
    if (
      !Number.isInteger(marker.rally) ||
      !range ||
      typeof range.start_seconds !== "number" ||
      typeof range.end_seconds !== "number" ||
      !Number.isFinite(range.start_seconds) ||
      !Number.isFinite(range.end_seconds) ||
      range.end_seconds < range.start_seconds
    ) {
      continue;
    }
    out.push({
      rally: marker.rally,
      start_seconds: range.start_seconds,
      end_seconds: range.end_seconds,
    });
  }
  return out;
}

/** What the completed panel says about a v1 job's original video. */
export interface SourceSummary {
  /** "47.7 MB" — from the decimal-string byte length, no Number(). */
  sizeLabel: string | null;
  /** Playable duration in ms, from ``playable_range``. */
  durationMs: number | null;
}

/**
 * The checked source, summarised for display. Null when the job is not
 * v1, not completed, or has no checked source yet.
 */
export function sourceSummary(job: ResultJob): SourceSummary | null {
  if (!isV1Job(job) || !job.source) return null;
  const range = job.source.playable_range;
  const durationMs =
    range &&
    Number.isFinite(range.start_seconds) &&
    Number.isFinite(range.end_seconds) &&
    range.end_seconds >= range.start_seconds
      ? Math.round((range.end_seconds - range.start_seconds) * 1000)
      : null;
  return {
    sizeLabel: formatDecimalBytes(job.source.byte_length),
    durationMs,
  };
}
